/// <amd-module name="TFS/Admin/Views/ProfileSettings/AboutView/AboutView" />
import React = require("react");

import * as AdminResources from "TFS/Admin/Views/ProfileSettings/AboutView/Resources";

import { AboutViewSource } from "Sources/AboutViewSource";
import { ServiceInstanceTypes } from "TFS/Admin/Common/Constants/Constants";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { ImageData, InputFileSelect } from "TFS/Admin/Common/Contracts/AvatarImagePicker.types";
import { MarketingPreferences } from "TFS/Admin/Common/Contracts/MarketingPreferences";
import { ProfileRegion } from "TFS/Admin/Common/Contracts/Profile";
import { Avatar, AvatarSize, UpdateUserParameters } from "TFS/Admin/Common/Contracts/User";
import { AdminFeatureTelemetryLogger, AdminTelemetry } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { ObservableValue } from "VSS/Core/Observable";
import { wait } from "VSS/Core/Util/Promise";
import { BearerTokenProvider } from "VSS/Platform/Authentication";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssRestClientOptions, TeamFoundationHostType } from "VSS/Platform/Context";
import { WebSessionToken } from "VSS/Platform/Contracts/Authentication";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { getIdentityImageUrl, IdentityImageSize } from "VSS/Platform/Identity";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { traceException } from "VSS/Platform/Trace";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown } from "VSSUI/DropDown";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { TextField } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { VssPersona } from "VSSUI/VssPersona";
import { isFeatureEnabled } from "VSS/Platform/Feature";


export interface IProfileUserData {
    displayName: string;
    email: string;
    emailConfirmationPending: boolean;
    region: {
        name: string;
        code: string;
    };
    identity: {
        id: string;
        subjectDescriptor: string;
    };
    oldProfileUrl: string;
}

export interface IProfileDelayedData {
    namedSessionToken?: WebSessionToken;
    marketingPreferences: MarketingPreferences;
}

// Using props for the sake of writing l0 tests
export interface IProfileAboutComponentProps {
    userProfileData: IProfileUserData | undefined;
    userProfileDelayedData: IProfileDelayedData | undefined;
}
interface IProfileAboutComponentState {
    profileAvatarUrl?: string;
    profileRegion: IProfileRegion;
    profileRegionsLoaded?: boolean;
    regionOptions: any[];
    confirmChecked: ObservableValue<boolean>;
    enableSaveBtn?: boolean;
    updateInProgress?: boolean;
    imgUpdateInProgress?: boolean;
    showChangeImageDialog?: boolean;
    error?: string;
    emailPendingConfirm?: boolean;
    emailInvalidMsg?: string;
}

// This is region/country value
interface IProfileRegion {
    name: string;
    code: string;
}

const dataProviderId = "ms.vss-admin-web.profile-about-view-data-provider";
const namedSessionTokenDataProviderId = "ms.vss-admin-web.profile-named-session-token-data-provider";
const progressPollInterval = 500;
// Lazy regex - added basic check on the client. As email regex can get really complicated, let the server do the detailed validation.
const emailRegex = /^\S+@\S+\.\S+/;

export class ProfileAboutComponent extends VssComponent<IProfileAboutComponentProps, IProfileAboutComponentState> {
    private profileNameChanged: boolean = false;
    private contactEmailChanged: boolean = false;
    private regionChanged: boolean = false;
    private marketingOptInCheckBoxChanged: boolean = false;
    private userProfileData: IProfileUserData;
    private namedSessionToken?: WebSessionToken;
    private regionSelection = new DropdownSelection();
    private marketingPreferences: MarketingPreferences;
    private editImageEl: HTMLElement;
    private isProfileSyncEnabled :boolean;

    private profileNameTextField: ObservableValue<string>;
    private contactEmailTextField: ObservableValue<string>;

    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.context.pageContext);

    private userClientSource: AboutViewSource;
    private aexClientSource: AboutViewSource;

    constructor(props: IProfileAboutComponentProps, context: IVssComponentContext) {
        super(props, context);

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        this.userProfileData = this.props.userProfileData || contributionService.getData<IProfileUserData>(dataProviderId)!;
        if (this.props.userProfileDelayedData) {
            this.marketingPreferences = this.props.userProfileDelayedData.marketingPreferences;
        }

        if (this.userProfileData) {
            this.state = {
                confirmChecked: this.marketingPreferences
                    ? new ObservableValue<boolean>(this.marketingPreferences.azureDevOps)
                    : new ObservableValue<boolean>(false),
                profileRegion: { code: this.userProfileData.region.code, name: this.userProfileData.region.name },
                regionOptions: [],
                emailPendingConfirm: this.userProfileData.emailConfirmationPending
            };

            this.profileNameTextField = new ObservableValue<string>(this.userProfileData.displayName);
            this.contactEmailTextField = new ObservableValue<string>(this.userProfileData.email);

            this.isProfileSyncEnabled = isFeatureEnabled(context.pageContext, "ms.vss-admin-web.user-profile-sync-feature", false);
        }
    }

    public componentDidMount() {
        super.componentDidMount();

        const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(Area.Admin, Feature.ProfileAboutView);

        if (this.userProfileData) {
            this._updateProfilePersona();
            this._getDelayedData();
        }
    }

    public render(): JSX.Element {
        if (!this.userProfileData) {
            return (
                <div className="flex-column profile-about">
                    <MessageBar className="message-bar" severity={MessageBarSeverity.Error}>
                        {AdminResources.DataProviderError}
                    </MessageBar>
                </div>
            );
        }
        const subheading = (heading: string) => <h5 className="fontSizeM">{heading}</h5>;
        const showMarketingMessage = this.marketingPreferences
            ? this.marketingPreferences.visualStudio || this.marketingPreferences.visualStudioSubscriptions
            : false;
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow">
                    <div className="flex-column profile-about">
                        {this.state.error ? (
                            <MessageBar
                                severity={MessageBarSeverity.Error}
                                onDismiss={() => this.setState({ error: undefined })}
                                className="message-bar"
                            >
                                {this.state.error}
                            </MessageBar>
                        ) : null}
                        <h2 className="title-l">{AdminResources.Title}</h2>
                        {this.state.profileAvatarUrl ? (
                            <div>
                                {subheading(AdminResources.PersonaLabel)}
                                <div className="profile-persona-section flex-row flex-center">
                                    <div className="flex flex-center justify-center">
                                        {this.state.imgUpdateInProgress && <Spinner size={SpinnerSize.medium} className="img-spinner" />}
                                        {!this.isProfileSyncEnabled && !this.state.imgUpdateInProgress && this.marketingPreferences ? (
                                            <div
                                                className="persona-list-element profile-card-tab-element image-container"
                                                aria-label={AdminResources.ChooseImage}
                                                onClick={this._onChangeImageClick}
                                                data-is-focusable={true}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={this._onChangeImageKeyDown}
                                                ref={el => {
                                                    this.editImageEl = el as HTMLElement;
                                                }}>
                                                {this._getProfileImage()}
                                            </div>
                                        ) : (
                                            this._getProfileImage()
                                        )}
                                    </div>
                                    <Button
                                        disabled={this.state.imgUpdateInProgress || !this.marketingPreferences || this.isProfileSyncEnabled}
                                        onClick={() => {
                                            this._onChangeImageClick();
                                        }}
                                        className="change-image-button"                                        
                                    >
                                        {AdminResources.ChooseImage}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <span>
                                <Spinner size={SpinnerSize.large} />
                            </span>
                        )}
                        <div>
                            {subheading(AdminResources.NameLabel)}
                            <TextField
                                value={this.profileNameTextField}
                                onChange={this._onNameChanged}
                                onBlur={this._onNameLostFocus}
                                ariaLabel={AdminResources.NameLabel}
                                maxLength={256}
                                disabled={!this.marketingPreferences || this.isProfileSyncEnabled}
                                    />
                        </div>
                        <div>
                            {subheading(AdminResources.EmailLabel)}
                            <FormItem message={this.state.emailInvalidMsg} error={!!this.state.emailInvalidMsg}>
                                <TextField
                                    value={this.contactEmailTextField}
                                    onChange={this._onEmailChanged}
                                    ariaLabel={AdminResources.EmailLabel}
                                    onBlur={this._onEmailLostFocus}
                                    disabled={!this.marketingPreferences || this.isProfileSyncEnabled}
                                />
                            </FormItem>
                            {this.state.emailPendingConfirm ? (
                                <Status {...Statuses.Warning} size={StatusSize.m} className="warning-status" text="Confirmation pending" />
                            ) : null}
                        </div>
                        <div>
                            {subheading(AdminResources.RegionLabel)}
                            <Dropdown
                                ariaLabel={AdminResources.RegionLabel}
                                items={this.state.regionOptions}
                                onSelect={this._onRegionChanged}
                                selection={this.regionSelection}
                                className="region-picker"
                                disabled={!this.state.profileRegionsLoaded || this.isProfileSyncEnabled}
                            />
                        </div>
                        <div>
                            {this.marketingPreferences ? (
                                <Checkbox
                                    label={AdminResources.TermsOfService}
                                    checked={this.state.confirmChecked.value}
                                    className="confirm-checkbox"
                                    onChange={this._onChecked}
                                />
                            ) : (
                                <Spinner className="margin-top-16" label={AdminResources.FetchingMarketingPreferences}></Spinner>
                            )}
                        </div>
                        <div className="save-btn">
                            <Button
                                primary={true}
                                disabled={!this.state.enableSaveBtn || this.state.updateInProgress || !!this.state.emailInvalidMsg}
                                onClick={this._onSaveProfileDetails}
                            >
                                {AdminResources.SaveButton}
                            </Button>
                            {this.state.updateInProgress ? <Spinner size={SpinnerSize.large} className="page-spinner" /> : null}
                        </div>
                        <div className="message-card">
                            <MessageCard severity={MessageCardSeverity.Info}>
                                <FormatComponent
                                    format={showMarketingMessage ? AdminResources.MarketingPreferencesInfo : AdminResources.RedirectProfileText}
                                    elementType="div"
                                >
                                    {this.userProfileData.oldProfileUrl ? (
                                        <Link
                                            href={this.userProfileData.oldProfileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={AdminResources.RedirectProfileLinkText}
                                        >
                                            {AdminResources.RedirectProfileLinkText}
                                        </Link>
                                    ) : (
                                        <span>{AdminResources.RedirectProfileLinkText}</span>
                                    )}
                                </FormatComponent>
                            </MessageCard>
                        </div>
                        {this.isProfileSyncEnabled && <div className="message-card">
                            <MessageCard severity={MessageCardSeverity.Info}>
                                {AdminResources.ProfileSyncMessage}
                            </MessageCard>
                        </div>}
                    </div>
                </Page>
                {this.state.showChangeImageDialog ? (
                    <WrappedComponent
                        dependencies={["ms.vss-features.ui-avatar-image-picker-content"]}
                        wrappedType={"vss.avatar-image-picker"}
                        wrappedProps={{
                            onCancel: (hasChanges: boolean) => {
                                this._cancelImageUpload(hasChanges);
                            },
                            onSave: (resetImage: boolean, imageData: ImageData) => {
                                this._saveImageAction(resetImage, imageData);
                            },
                            onFileSelected: (error: string, imageData: ImageData) => {
                                return this._onImageFileSelected(error, imageData);
                            },
                            onLoadDisplayAvatarUrl: this.state.profileAvatarUrl!,
                            title: AdminResources.PersonaLabel,
                            displayName: this.userProfileData.displayName,
                            showInitialsOnImageError: true
                        }}
                    />
                ) : null}
            </Surface>
        );
    }

    private _getProfileImage(): JSX.Element {
        return (
            <VssPersona
                identityDetailsProvider={{
                    getIdentityImageUrl: (size: number): string | undefined => {
                        return this.state.profileAvatarUrl;
                    },
                    getDisplayName: (): string => {
                        return this.userProfileData.displayName;
                    }
                }}
                suppressPersonaCard={true}
                imgAltText={this.userProfileData.displayName}
                size="extra-large"
                className="profile-persona"                
            />
        );
    }

    private _onNameChanged = (event: any, value: string) => {
        this.profileNameTextField.value = value;
        this.profileNameChanged = value !== (this.userProfileData.displayName || "") && value !== "";
        this._enableSaveButton(this.profileNameChanged || this.contactEmailChanged || this.regionChanged || this.marketingOptInCheckBoxChanged);
    };

    private _onNameLostFocus = (): void => {
        // Reset the display name if the value was cleared
        this.profileNameTextField.value = this.profileNameTextField.value || this.userProfileData.displayName;
    };

    private _onEmailLostFocus = (): void => {
        if (!this.contactEmailTextField.value && this.state.emailInvalidMsg) {
            this.setState({ emailInvalidMsg: undefined });
        }

        // Reset the email if the value was cleared
        this.contactEmailTextField.value = this.contactEmailTextField.value || this.userProfileData.email;
    };

    private _onEmailChanged = (event: any, value: string) => {
        this.contactEmailTextField.value = value;
        if (!emailRegex.test(value)) {
            this.setState({ emailInvalidMsg: AdminResources.EmailFormat });
        } else {
            if (this.state.emailInvalidMsg) {
                this.setState({ emailInvalidMsg: undefined });
            }
        }

        this.contactEmailChanged = value !== (this.userProfileData.email || "") && value !== "";
        this._enableSaveButton(this.profileNameChanged || this.contactEmailChanged || this.regionChanged || this.marketingOptInCheckBoxChanged);
    };

    private _enableSaveButton(enable: boolean): void {
        if (this.state.enableSaveBtn !== enable) {
            this.setState({ enableSaveBtn: enable });
        }
    }

    private _onRegionChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>) => {
        if (item) {
            this.setState({ profileRegion: { code: item.id, name: item.text ? item.text : "" } });
            this.regionChanged = item.id !== this.userProfileData.region.code;
        }
        this._enableSaveButton(this.profileNameChanged || this.contactEmailChanged || this.regionChanged || this.marketingOptInCheckBoxChanged);
    };

    private _onChecked = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean) => {
        this.marketingOptInCheckBoxChanged = checked ? !this.marketingPreferences.azureDevOps : this.marketingPreferences.azureDevOps;
        this.setState({ confirmChecked: new ObservableValue<boolean>(checked) });
        this._enableSaveButton(this.profileNameChanged || this.contactEmailChanged || this.regionChanged || this.marketingOptInCheckBoxChanged);
    };

    private _onChangeImageClick = (): void => {
        this.setState({ showChangeImageDialog: true });
        if (this.editImageEl) {
            this.editImageEl.blur();
        }
    };

    private _onChangeImageKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.keyCode === KeyCode.enter || event.keyCode === KeyCode.space) {
            this._onChangeImageClick();
        }
    };

    private async _saveImageAction(resetImage: boolean, imageData: ImageData) {
        if (resetImage) {
            this._removeImage();
        } else {
            this._saveImage(imageData);
        }
    }

    private _saveImage = async (imageData: ImageData) => {
        const avatarBlob: Avatar = { image: imageData.byteArray, isAutoGenerated: false, size: AvatarSize.Large, lastModified: new Date() };
        this.setState({ imgUpdateInProgress: true, showChangeImageDialog: false });

        if (!this.userClientSource) {
            this.userClientSource = new AboutViewSource(this.getRestClientOptions(ServiceInstanceTypes.Users));
        }
        const actionTelemetryLogger = this._startActionCapture("Set Profile Avatar", {});
        await this.userClientSource.setAvatar(avatarBlob, this.userProfileData.identity.subjectDescriptor, this.context).then(
            () => {
                this.setState({ imgUpdateInProgress: false }, () => this._updateProfilePersona());
            },
            reason => {
                this.setState({ imgUpdateInProgress: false, error: reason.message || String(reason) }, () => this._updateProfilePersona());
                actionTelemetryLogger.captureError(reason.message || String(reason));
            }
        );
    };

    private _removeImage = async () => {
        this.setState({ imgUpdateInProgress: true, showChangeImageDialog: false });
        if (!this.userClientSource) {
            this.userClientSource = new AboutViewSource(this.getRestClientOptions(ServiceInstanceTypes.Users));
        }

        const actionTelemetryLogger = this._startActionCapture("Remove Profile Avatar", {});
        await this.userClientSource.deleteAvatar(this.userProfileData.identity.subjectDescriptor, this.context).then(
            async () => {
                this.setState({ imgUpdateInProgress: false }, () => this._updateProfilePersona());
            },
            reason => {
                this.setState({ imgUpdateInProgress: false, error: reason.message || String(reason) }, () => this._updateProfilePersona());
                actionTelemetryLogger.captureError(reason.message || String(reason));
            }
        );
    };

    private _updateProfilePersona() {
        if (this.userProfileData.identity.id) {
            const imageURL = getIdentityImageUrl(this.context.pageContext, this.userProfileData.identity.id, IdentityImageSize.large);
            this.setState({
                profileAvatarUrl: imageURL + `&` + new Date().getTime() // Adding date attribute to avoid caching issue while the image is rendering.
            });
        }
    }

    private _cancelImageUpload = (hasChanges: boolean) => {
        this.setState({ showChangeImageDialog: false });
    };

    private _onImageFileSelected(error: string, imageData: ImageData): InputFileSelect {
        const result = {} as InputFileSelect;

        // File validation error from image select.
        if (error) {
            result.previewImage = false;
            result.error = error;
        } else {
            result.previewImage = true;
            result.error = "";
        }

        return result;
    }

    private _loadProfileRegions = async () => {
        this.setState({ profileRegionsLoaded: false });
        if (!this.aexClientSource) {
            this.aexClientSource = new AboutViewSource(this.getRestClientOptions(ServiceInstanceTypes.AEX));
        }

        const actionTelemetryLogger = this._startActionCapture("Loading regions/country", {});
        await this.aexClientSource.getRegions(this.context).then(
            async opref => {
                this.setState({ profileRegionsLoaded: true });
                this._loadRegionOptions(opref.regions);
            },
            reason => {
                this.setState({ profileRegionsLoaded: false, error: reason.message || String(reason) });
                actionTelemetryLogger.captureError(reason.message);
            }
        );
    };

    private _loadRegionOptions(regions: ProfileRegion[]): void {
        const dropDownOptions = [];
        if (regions) {
            dropDownOptions.push({ id: this.state.profileRegion.code, text: this.state.profileRegion.name });
            for (const index in regions) {
                const id = regions[index].code;
                if (id === this.state.profileRegion.code) {
                    dropDownOptions[0].text = regions[index].name;
                    this.setState(prevState => ({
                        profileRegion: {
                            ...prevState.profileRegion,
                            name: regions[index].name
                        }
                    }));
                } else {
                    dropDownOptions.push({ id: id, text: regions[index].name });
                }
            }
        }

        this.setState({ regionOptions: this.state.regionOptions.concat(dropDownOptions) }, () => {
            dropDownOptions.length > 0 && this.regionSelection.select(0);
        });
    }

    private _onSaveProfileDetails = async (): Promise<void> => {
        if (this.profileNameChanged || this.contactEmailChanged || this.regionChanged) {
            await this._populateAndSaveUserParams();
        }

        if (this.marketingOptInCheckBoxChanged) {
            this.marketingOptInCheckBoxChanged = false;
            await this._updateMarketingPreferences();
        }
    };

    private _populateAndSaveUserParams = (): void => {
        const updateNode: UpdateUserParameters = { properties: {} };

        if (this.profileNameChanged) {
            const newProfileName = this.profileNameTextField && this.profileNameTextField.value;
            if (newProfileName != null) {
                this._startActionCapture("Update Profile Name", {});
                updateNode.properties.DisplayName = newProfileName;
            }
        }

        if (this.contactEmailChanged) {
            const newEmail = this.contactEmailTextField && this.contactEmailTextField.value;
            if (newEmail != null) {
                this._startActionCapture("Update Profile Mail", {});
                updateNode.properties.UnconfirmedMail = newEmail;
            }
        }

        if (this.regionChanged) {
            this._startActionCapture("Update Profile Region", {});
            updateNode.properties.Country = this.state.profileRegion.code;
        }

        this.profileNameChanged = this.contactEmailChanged = this.regionChanged = false;
        this._updateProfile(updateNode);
    };

    private _updateProfile = async (updateUserProperties: UpdateUserParameters) => {
        this.setState({ updateInProgress: true });
        if (!this.userClientSource) {
            this.userClientSource = new AboutViewSource(this.getRestClientOptions(ServiceInstanceTypes.Users));
        }

        const actionTelemetryLogger = this._startActionCapture("Update Profile Properties", {});

        this.userClientSource.updateUser(updateUserProperties, this.userProfileData.identity.subjectDescriptor, this.context).then(
            async User => {
                this._refresh();
            },
            reason => {
                this.setState({ updateInProgress: false, error: reason.message || String(reason), enableSaveBtn: true });
                actionTelemetryLogger.captureError(reason.message || String(reason));
            }
        );
    };

    private _updateMarketingPreferences = async () => {
        this.setState({ updateInProgress: true });
        if (!this.userClientSource) {
            this.userClientSource = new AboutViewSource(this.getRestClientOptions(ServiceInstanceTypes.Users));
        }
        const actionTelemetryLogger = this._startActionCapture("Update Profile AzDevOps Marketing OptIn", {});
        this.userClientSource
            .setMarketingPreferences(
                {
                    azureDevOps: this.state.confirmChecked.value,
                    visualStudio: this.marketingPreferences.visualStudio,
                    visualStudioSubscriptions: this.marketingPreferences.visualStudioSubscriptions
                } as MarketingPreferences, // Had to set the visualStudio and visualStudioSubscriptions props as well as they are defaulting to false in the REST call.
                this.userProfileData.identity.subjectDescriptor,
                this.context
            )
            .then(
                async () => {
                    this._refresh();
                },
                reason => {
                    this.setState({ updateInProgress: false, error: reason.message || String(reason), enableSaveBtn: true });
                    actionTelemetryLogger.captureError(reason.message || String(reason));
                }
            );
    };

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.ProfileAboutView, Feature.ProfileAboutView, action, properties);
    };

    private getRestClientOptions(serviceInstanceType: string): IVssRestClientOptions {
        return {
            serviceInstanceType: serviceInstanceType,
            hostType: TeamFoundationHostType.Deployment,
            authTokenProvider: this.namedSessionToken ? new BearerTokenProvider(this.namedSessionToken.token) : undefined
        };
    }

    private async _getDelayedData() {
        let delayedData: IProfileDelayedData | undefined;
        if (this.props.userProfileDelayedData) {
            delayedData = this.props.userProfileDelayedData;
        } else {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            delayedData = await contributionService.getDataAsync<IProfileDelayedData>(
                namedSessionTokenDataProviderId,
                undefined /*RequestParameters*/,
                true /*ignoreCache*/
            );
        }

        if (delayedData) {
            this.namedSessionToken = delayedData.namedSessionToken;
            this.marketingPreferences = delayedData.marketingPreferences;
            !this.props.userProfileData ? this._loadProfileRegions() : this._loadRegionOptions([this.props.userProfileData.region]);
            this.setState({ confirmChecked: new ObservableValue<boolean>(this.marketingPreferences.azureDevOps) });
        }
    }

    private async _refresh(): Promise<void> {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const actionTelemetryLogger = this._startActionCapture("Fetch new data after profile update", {});
        try {
            let i = 0;
            while (this.state.updateInProgress === true && i++ < 100) {
                const newData = await contributionService.getDataAsync<IProfileUserData>(
                    dataProviderId,
                    undefined /*RequestParameters*/,
                    true /*ignoreCache*/
                );
                if (newData) {
                    this.userProfileData = newData;
                    this.regionSelection.clear();
                    this.marketingPreferences.azureDevOps = this.state.confirmChecked.value;
                    this.setState(
                        {
                            profileRegion: { code: newData.region.code, name: newData.region.name },
                            regionOptions: [],
                            updateInProgress: false,
                            imgUpdateInProgress: false,
                            enableSaveBtn: false,
                            emailPendingConfirm: newData.emailConfirmationPending,
                            error: undefined,
                            emailInvalidMsg: undefined
                        },
                        () => this._setInitialValues()
                    );
                }
                await wait(progressPollInterval);
            }
        } catch (ex) {
            actionTelemetryLogger.captureError(ex.message);
            traceException(this.context.pageContext, "admin", "profileaboutview", ex);

            this.setState({
                error: AdminResources.WaitError,
                updateInProgress: false,
                imgUpdateInProgress: false
            });
        }
    }

    private _setInitialValues() {
        this.profileNameTextField = new ObservableValue<string>(this.userProfileData.displayName);
        this.contactEmailTextField = new ObservableValue<string>(this.userProfileData.email);
        this._updateProfilePersona();
        this._loadProfileRegions();
    }
}

Components.add("AboutView", ProfileAboutComponent);
